import ImageWithLoader from '@/components/common/images/ImageWithLoader';
import { CloudArrowUpIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';
import { Button } from '../button';

interface MediaInputProps<T extends FieldValues> {
  value: File[];
  setValue: UseFormSetValue<T>;
  error?: string;
  name: string;
  multiple?: boolean;
  maxFiles?: number;
  // eslint-disable-next-line no-unused-vars
  validateFileName?: (fileName: string) => Promise<{ error?: string; isValid: boolean }>;
  fileNameValidationMessage?: string;
  checkDuplicates?: boolean;
  // eslint-disable-next-line no-unused-vars
  setDuplicates?: (duplicates: string[]) => void;
  duplicateFileNames?: string[];
  accept?: string;
  disabled?: boolean;
}

export function MediaInput<T extends FieldValues>({
  value,
  setValue,
  error,
  name,
  multiple = false,
  maxFiles,
  validateFileName,
  fileNameValidationMessage = 'El nombre del archivo no cumple con el formato requerido',
  checkDuplicates = false,
  setDuplicates,
  duplicateFileNames = [],
  accept = 'image/*',
  disabled = false,
}: MediaInputProps<T>) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [files, setFiles] = useState<File[]>(value);
  const [fileNameError, setFileNameError] = useState<string>('');

  const acceptAllowsVideo = accept?.includes('video') ?? false;
  const acceptAllowsImage = !accept || accept.includes('image');

  useEffect(() => {
    setFiles(value || []);
  }, [value]);

  async function handleImageChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (disabled) return;
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length > 0) {
      await handleFileSelection(selectedFiles);
    }

    event.target.value = '';
  }

  async function handleFileSelection(newFiles: File[]) {
    if (disabled) return;
    const mediaFiles = newFiles.filter((file) => {
      if (!file?.type) return false;
      if (file.type.startsWith('image/')) return acceptAllowsImage;
      if (file.type.startsWith('video/')) return acceptAllowsVideo;
      return false;
    });
    if (mediaFiles.length === 0) {
      return;
    }

    if (validateFileName) {
      const invalidFiles: { error?: string; isValid: boolean }[] = [];
      const resultPromises = mediaFiles.map(async (file) => {
        const validation = await validateFileName(file.name);
        if (!validation.isValid) {
          invalidFiles.push(validation);
        }
      });
      await Promise.all(resultPromises);

      if (invalidFiles.length > 0) {
        const errorMessages = invalidFiles.map((file) => file.error).filter(Boolean);
        setFileNameError(errorMessages.join('\n') || fileNameValidationMessage);
        return;
      }
    }

    setFileNameError('');

    if (multiple) {
      let uniqueNewFiles = mediaFiles;
      if (checkDuplicates && setDuplicates) {
        const existingNames = new Set([...files.map((f) => f.name), ...duplicateFileNames]);
        uniqueNewFiles = mediaFiles.filter((f) => !existingNames.has(f.name));
        const duplicatedNames = mediaFiles
          .filter((f) => existingNames.has(f.name))
          .map((f) => f.name);

        if (uniqueNewFiles.length < mediaFiles.length) {
          setDuplicates(duplicatedNames);
        } else {
          setDuplicates([]);
        }

        if (uniqueNewFiles.length === 0) return;
      }

      const updatedFiles =
        maxFiles && maxFiles > 0
          ? [...files, ...uniqueNewFiles].slice(0, maxFiles)
          : [...files, ...uniqueNewFiles];
      setFiles(updatedFiles);
      setValue(name as Path<T>, updatedFiles as PathValue<T, Path<T>>);
    } else {
      const file = mediaFiles[0];
      if (file) {
        setValue(name as Path<T>, file as PathValue<T, Path<T>>);
        setFiles([file]);
      }
    }
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragOver(false);
  }

  async function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0) {
      await handleFileSelection(droppedFiles);
    }
  }

  return (
    <div className="self-stretch inline-flex flex-col justify-start items-start gap-1.5 mt-2">
      <div
        className={`py-6 px-12 bg-white rounded-2xl outline-1 -outline-offset-1 inline-flex flex-col justify-center items-center gap-4 w-full outline-dashed transition-colors ${
          disabled
            ? 'outline-gray-200 bg-gray-50'
            : error
              ? 'input-error outline-error-300 focus-within:outline-error-300'
              : isDragOver
                ? 'outline-blue-500 bg-blue-50'
                : 'outline-gray-300 focus-within:outline-blue-300 hover:outline-blue-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="self-stretch py-3 flex flex-col justify-center items-center gap-2.5">
          {(files.length > 0 || value.length > 0) && !multiple ? (
            <ImageWithLoader
              src={URL.createObjectURL(files[0] || value[0] || '')}
              alt="Archivo seleccionado"
              className="w-36 h-36"
              imageclassname="object-cover rounded-2xl"
              width={144}
              height={144}
              fill
            />
          ) : (
            <div className="p-3 bg-gray-50 rounded-full inline-flex justify-start items-center gap-2.5">
              <div data-style="outlined" className="w-6 h-6 relative overflow-hidden">
                <CloudArrowUpIcon className="w-6 h-6 left-0 top-1 absolute text-blue-600" />
              </div>
            </div>
          )}
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
          <div className="self-stretch flex flex-col justify-start items-center gap-2">
            <div className="self-stretch text-center justify-center text-gray-800 text-base font-medium leading-normal">
              {multiple
                ? 'Arrastra y suelta tus archivos aquí'
                : 'Arrastra y suelta tu archivo aquí'}
            </div>
          </div>
          <div className="self-stretch flex flex-col justify-start items-center gap-2">
            <div className="self-stretch text-center justify-center text-gray-800 text-sm font-normal leading-tight">
              {multiple
                ? 'o haz clic para seleccionar archivos'
                : 'o haz clic para seleccionar archivo'}
            </div>
          </div>
        </div>
        <Button
          variant="primaryClear"
          onClick={() => document.getElementById('media-input')?.click()}
          disabled={disabled}
        >
          {multiple ? 'Selecciona archivos' : 'Selecciona archivo'}
        </Button>
        <input
          type="file"
          id="media-input"
          accept={accept}
          multiple={multiple}
          onChange={handleImageChange}
          className="hidden w-0 h-0"
          disabled={disabled}
        />
      </div>
      {(error || fileNameError) && (
        <div className="self-stretch justify-start text-error-500 text-xs font-normal">
          {error ? error : <div className="whitespace-pre-line">{fileNameError}</div>}
        </div>
      )}
    </div>
  );
}
